'use client'

import type { StaticImport } from 'next/dist/shared/lib/get-img-props'
import { useEffect, useRef, useState } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { ChevronDown, LogIn, Menu, X } from 'lucide-react'

import { resetAnalytics } from '@package/analytics/client'
import PiBoLogo from '@ui/assets/logo/logo.svg'
import { Button } from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import { buildUrl } from '@helper/utils/format'
import cn from '@config/tailwind/cn'

import { authClient } from '~/auth/client'
import { poppins } from '~/font'
import { useAuthModals } from './auth-modals'
import { LANDING_ROUTES } from './links'

const SEKOLAH_ITEMS = [
  { href: LANDING_ROUTES.guru, title: 'Guru', description: 'Buku dan aktivitas siap pakai untuk kelasmu' },
  { href: LANDING_ROUTES.sekolah, title: 'Pengelola Sekolah', description: 'Pantau literasi seluruh sekolah' },
  { href: LANDING_ROUTES.sumberDaya, title: 'Sumber daya gratis', description: 'Materi ajar dan panduan singkat' },
] as const

const TOP_ITEMS = [
  { href: LANDING_ROUTES.orangTua, title: 'Orang tua' },
  { href: LANDING_ROUTES.beasiswa, title: 'Beasiswa' },
  { href: LANDING_ROUTES.jelajahi, title: 'Jelajahi' },
] as const

const NAV_LINK = cn(
  typographyVariants({ variant: 'label-lg' }),
  'text-neutral-black hover:bg-blue-10 inline-flex min-h-11 items-center gap-1.5 rounded-full px-3.5 py-2.5 hover:text-blue-100',
)

const MENU_ITEM = 'hover:bg-blue-10 text-neutral-black flex flex-col gap-0.5 rounded-xl px-3.5 py-3'

function isCurrent(pathname: string, href: string) {
  return href === '/' ? pathname === '/' : pathname === href || pathname.startsWith(`${href}/`)
}

function AccountActions({ stacked }: { stacked?: boolean }) {
  const { data: session } = authClient.useSession()
  const { openSignIn, openSignUp } = useAuthModals()

  if (session) {
    return (
      <div className={cn('flex items-center gap-2', stacked && 'flex-col items-stretch')}>
        <Button
          variant='ghost'
          onClick={async () => {
            // Clear PostHog identity so the next user on a shared device starts clean.
            resetAnalytics()
            await authClient.signOut()
          }}
        >
          Keluar
        </Button>
        <Button variant='secondary' asChild>
          <Link href={buildUrl('account', '/check-user')} prefetch>
            Menuju aplikasi <LogIn className='size-4' />
          </Link>
        </Button>
      </div>
    )
  }

  return (
    <div className={cn('flex items-center gap-2', stacked && 'flex-col items-stretch')}>
      {!stacked && <Button onClick={openSignUp}>Daftar</Button>}
      <Button variant='secondary' onClick={openSignIn}>
        Login
      </Button>
    </div>
  )
}

export function SiteNav({ className }: { className?: string }) {
  const pathname = usePathname()
  const { data: session } = authClient.useSession()
  const { openSignUp } = useAuthModals()
  const [sekolahOpen, setSekolahOpen] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const sekolahRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (sekolahRef.current && !sekolahRef.current.contains(event.target as Node)) setSekolahOpen(false)
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setSekolahOpen(false)
        setMobileOpen(false)
      }
    }
    document.addEventListener('click', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('click', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [])

  const sekolahActive = SEKOLAH_ITEMS.some((item) => isCurrent(pathname, item.href))

  return (
    <header className={cn('bg-neutral-white border-grey-20 z-50 border-b', poppins.className, className)}>
      <div className='mx-auto flex max-w-320 items-center gap-6 px-4 py-3 md:px-8'>
        <Link href='/' aria-label='PiBo beranda' className='flex min-h-11 min-w-11 items-center'>
          <Image src={PiBoLogo as StaticImport} alt='PiBo' height={36} className='h-9 w-auto' priority />
        </Link>

        <nav aria-label='Menu utama' className='hidden flex-1 items-center gap-1 lg:flex'>
          <div ref={sekolahRef} className='relative'>
            <button
              type='button'
              className={cn(NAV_LINK, sekolahActive && 'text-blue-100')}
              aria-expanded={sekolahOpen}
              aria-controls='sekolah-menu'
              aria-haspopup='true'
              onClick={() => setSekolahOpen((open) => !open)}
            >
              Sekolah
              <ChevronDown className={cn('size-4 transition-transform', sekolahOpen && 'rotate-180')} />
            </button>
            {sekolahOpen && (
              <div
                id='sekolah-menu'
                className='bg-neutral-white border-grey-20 absolute top-13 left-0 flex w-80 flex-col rounded-2xl border p-2 shadow-xl'
              >
                {SEKOLAH_ITEMS.map((item) => (
                  <Link
                    key={item.href}
                    href={item.href}
                    className={MENU_ITEM}
                    onClick={() => setSekolahOpen(false)}
                    aria-current={isCurrent(pathname, item.href) ? 'page' : undefined}
                  >
                    <span className={typographyVariants({ variant: 'title-md' })}>{item.title}</span>
                    <span className={cn(typographyVariants({ variant: 'body-md' }), 'text-navy-60')}>
                      {item.description}
                    </span>
                  </Link>
                ))}
              </div>
            )}
          </div>
          {TOP_ITEMS.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              className={cn(NAV_LINK, isCurrent(pathname, item.href) && 'text-blue-100')}
              aria-current={isCurrent(pathname, item.href) ? 'page' : undefined}
            >
              {item.title}
            </Link>
          ))}
        </nav>

        <div className='hidden lg:block'>
          <AccountActions />
        </div>

        <div className='ml-auto flex items-center gap-2 lg:hidden'>
          {!session && <Button onClick={openSignUp}>Daftar</Button>}
          <button
            type='button'
            aria-label={mobileOpen ? 'Tutup menu' : 'Buka menu'}
            aria-expanded={mobileOpen}
            aria-controls='mobile-menu'
            onClick={() => setMobileOpen((open) => !open)}
            className='border-grey-20 text-neutral-black flex size-11 items-center justify-center rounded-xl border'
          >
            {mobileOpen ? <X className='size-5' /> : <Menu className='size-5' />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <nav
          id='mobile-menu'
          aria-label='Menu utama'
          className='border-grey-20 flex flex-col gap-0.5 border-t px-4 pt-2 pb-5 lg:hidden'
        >
          <span className={cn(typographyVariants({ variant: 'label-sm' }), 'text-navy-60 px-3.5 pt-3 pb-1 uppercase')}>
            Sekolah
          </span>
          {SEKOLAH_ITEMS.map((item) => (
            <Link key={item.href} href={item.href} className={MENU_ITEM} onClick={() => setMobileOpen(false)}>
              <span className={typographyVariants({ variant: 'title-md' })}>{item.title}</span>
            </Link>
          ))}
          <div className='bg-grey-20 my-2 h-px' />
          {TOP_ITEMS.map((item) => (
            <Link key={item.href} href={item.href} className={MENU_ITEM} onClick={() => setMobileOpen(false)}>
              <span className={typographyVariants({ variant: 'title-md' })}>{item.title}</span>
            </Link>
          ))}
          <div className='mt-2'>
            <AccountActions stacked />
          </div>
        </nav>
      )}
    </header>
  )
}
